import { MediaTypes } from "@coworker/app/src/components/RoomSettings/constants";
import {
  getMediaTypeText,
  getAreaDisplayName,
} from "@coworker/app/src/components/RoomSettings/tools";
import { MediasAndArticleCountResponse } from "@coworker/app/src/components/RoomSettings/types/media";
import { MediaOrderedDetails } from "@coworker/apprestructured/src/task-management/types/TaskDetailTypes";
import { FixaListView } from "@coworker/apprestructured/src/shared/wrappers/FixaListView/FixaListView";
import { FixaListViewItem } from "@coworker/apprestructured/src/shared/wrappers/FixaListView/FixaListViewItem";
import { FixaText } from "@coworker/apprestructured/src/shared/wrappers/FixaText/FixaText";
import { useTranslation } from "@coworker/locales";
import { ReactNode } from "react";

// @ingka/list-view clones direct children with id/name/size/showDivider, which React.Fragment rejects.
const ListViewItemGroup = ({ children }: { children: ReactNode }) => (
  <>{children}</>
);

interface MediaGroupedListViewProps {
  groupedMediasByType: Record<string, MediasAndArticleCountResponse[]>;
  mediasOrderedDetails?: MediaOrderedDetails[] | null;
  renderControls?: (
    media: MediasAndArticleCountResponse,
    details?: MediaOrderedDetails
  ) => ReactNode;
  headerMessage?: ReactNode;
  showCheckbox?: boolean;
  showChevron?: boolean;
  showQuantityLabel?: boolean;
  selectedMediaIds?: Array<{ mediaId: string; articleId: string }>;
  onMediaSelectionChange?: (
    mediaId: string,
    articleId: string,
    checked: boolean
  ) => void;
  onItemClick?: (media: MediasAndArticleCountResponse) => void;
  itemClassName?: string;
  headerClassName?: string;
  additionalProps?: {
    inset?: boolean;
  };
}

export const MediaGroupedListView = ({
  groupedMediasByType,
  mediasOrderedDetails,
  renderControls,
  headerMessage,
  showCheckbox = false,
  showChevron = false,
  showQuantityLabel = true,
  selectedMediaIds = [],
  onMediaSelectionChange,
  onItemClick,
  itemClassName,
  headerClassName,
  additionalProps,
}: MediaGroupedListViewProps) => {
  const { t } = useTranslation();

  const isSelected = (mediaId: string, articleId: string): boolean =>
    selectedMediaIds.some(
      (key) => key.mediaId === mediaId && key.articleId === articleId
    );

  const detailsByCompositeKey = new Map(
    Array.isArray(mediasOrderedDetails)
      ? mediasOrderedDetails.map(
          ({ id, article_id, pieces, pieces_ordered, ptags_ordered }) => [
            `${id}:${article_id}`,
            { id, article_id, pieces, pieces_ordered, ptags_ordered },
          ]
        )
      : []
  );

  // If ROOM media type is present, we convert it to HOME for display purposes, as per the requirement
  // to treat ROOM as HOME in this context.
  const mediaTypeWithSpecialRoomToHomeConversion = (
    mediaType: string
  ): MediaTypes =>
    mediaType === MediaTypes.ROOM ? MediaTypes.HOME : (mediaType as MediaTypes);

  return (
    <>
      {headerMessage}
      {Object.entries(groupedMediasByType).map(([mediaType, medias]) => (
        <FixaListView key={mediaType} id={`media-type-${mediaType}`}>
          <FixaListViewItem
            size="small"
            description={getMediaTypeText(
              mediaTypeWithSpecialRoomToHomeConversion(mediaType)
            )}
            key={`${mediaType}-header`}
            className={headerClassName}
            showDivider={false}
          />
          {medias.map((media) => {
            const compositeKey = `${media.mediaId}:${media.articleId}`;
            const details = detailsByCompositeKey.get(compositeKey);
            const pieces = details?.pieces ?? media.nbrArticles;
            const isArticlePartOfLocalCombination =
              !!media.parentArticleCombinationNumber;
            const localCombinationTitle = isArticlePartOfLocalCombination
              ? `- ${t("partOfLocalCombinationString")}`
              : "";
            const title =
              mediaType === MediaTypes.ROOM && media.parentMediaName
                ? `${media.parentMediaName} ${localCombinationTitle}`
                : `${media.name} ${localCombinationTitle}`;

            const description =
              mediaType === MediaTypes.ROOM && media.parentMediaName
                ? `${media.name}${media.groupName ? ` > ${media.groupName}` : ""}`
                : media.groupName;

            const areaDisplayName = getAreaDisplayName(media.area, t);

            const isItemSelected = isSelected(media.mediaId, media.articleId);

            return (
              <ListViewItemGroup key={compositeKey}>
                <FixaListViewItem
                  id={`media-item-${compositeKey}`}
                  size="medium"
                  showDivider={renderControls ? false : undefined}
                  title={<FixaText textStyle="Heading.S">{title}</FixaText>}
                  description={
                    <FixaText textStyle="Body.M">{description}</FixaText>
                  }
                  quantityLabel={
                    showQuantityLabel ? (
                      <FixaText textStyle="Body.M">
                        {t("piecesCountString", { count: pieces })}
                      </FixaText>
                    ) : undefined
                  }
                  addOn={
                    areaDisplayName ? (
                      <FixaText textStyle="Body.S">{areaDisplayName}</FixaText>
                    ) : undefined
                  }
                  control={
                    showCheckbox
                      ? "checkbox"
                      : showChevron
                        ? "navigational"
                        : undefined
                  }
                  disabled={isArticlePartOfLocalCombination}
                  controlIcon={showChevron ? "chevronRight" : undefined}
                  controlProps={
                    showCheckbox
                      ? {
                          checked: isItemSelected,
                          value: `${media.mediaId}-${media.articleId}`,
                        }
                      : undefined
                  }
                  onChange={
                    showCheckbox
                      ? () =>
                          onMediaSelectionChange?.(
                            media.mediaId,
                            media.articleId,
                            !isItemSelected
                          )
                      : undefined
                  }
                  onClick={showChevron ? () => onItemClick?.(media) : undefined}
                  className={itemClassName}
                  {...additionalProps}
                />
                {renderControls?.(media, details)}
              </ListViewItemGroup>
            );
          })}
        </FixaListView>
      ))}
    </>
  );
};
